import { User } from '@/api/models';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Pagination } from '@/components/ui/Pagination';
import { SendNotificationModal } from '@/pages/notifications/components/SendNotificationModal';
import {
  Notification,
  useNotifications,
} from '@/pages/notifications/hooks/useNotifications';
import { NotificationType } from '@coffice/shared/api';
import {
  mdiBell,
  mdiBullhorn,
  mdiCalendarClock,
  mdiCheck,
  mdiClose,
  mdiSend,
} from '@mdi/js';
import { DateTime } from 'luxon';
import * as React from 'react';

interface UserNotificationsTabProps {
  user: User;
}

const typeLabels: Record<NotificationType, string> = {
  [NotificationType.plan_reminder]: 'Plan Reminder',
  [NotificationType.plan_created_by_admin]: 'Plan Created',
  [NotificationType.review_reminder]: 'Review Reminder',
  [NotificationType.announcement]: 'Announcement',
  [NotificationType.marketing]: 'Marketing',
  [NotificationType.system]: 'System',
};

type BadgeColor = 'blue' | 'green' | 'yellow' | 'purple' | 'gray';

const typeColors: Record<NotificationType, BadgeColor> = {
  [NotificationType.plan_reminder]: 'blue',
  [NotificationType.plan_created_by_admin]: 'green',
  [NotificationType.review_reminder]: 'yellow',
  [NotificationType.announcement]: 'purple',
  [NotificationType.marketing]: 'purple',
  [NotificationType.system]: 'gray',
};

const typeIcons: Record<NotificationType, string> = {
  [NotificationType.plan_reminder]: mdiCalendarClock,
  [NotificationType.plan_created_by_admin]: mdiCalendarClock,
  [NotificationType.review_reminder]: mdiCalendarClock,
  [NotificationType.announcement]: mdiBullhorn,
  [NotificationType.marketing]: mdiBullhorn,
  [NotificationType.system]: mdiBell,
};

export const UserNotificationsTab: React.FC<UserNotificationsTabProps> = ({
  user,
}) => {
  const [page, setPage] = React.useState(1);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const limit = 10;

  const { data, isLoading } = useNotifications({
    page,
    limit,
    userId: user.id,
  });

  const notifications = data?.notifications ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  if (isLoading) {
    return (
      <div className='p-6'>
        <div className='animate-pulse space-y-4'>
          <div className='h-10 bg-grey-200 rounded w-32' />
          <div className='h-16 bg-grey-200 rounded' />
          <div className='h-16 bg-grey-200 rounded' />
          <div className='h-16 bg-grey-200 rounded' />
        </div>
      </div>
    );
  }

  return (
    <div className='p-6 space-y-4'>
      {/* Header with Send Button */}
      <div className='flex items-center justify-between'>
        <div className='text-sm text-grey-600'>
          {total} notification{total !== 1 ? 's' : ''} sent
        </div>
        <Button
          icon={mdiSend}
          label='Send Notification'
          color='primary'
          small
          onClick={() => setIsModalOpen(true)}
        />
      </div>

      {/* Notifications List */}
      {notifications.length === 0 ? (
        <div className='text-center py-12'>
          <Icon
            path={mdiBell}
            size={48}
            className='mx-auto mb-4 text-grey-300'
          />
          <p className='text-grey-500'>
            No notifications sent to this user yet.
          </p>
          <Button
            icon={mdiSend}
            label='Send First Notification'
            color='primary'
            className='mt-4'
            onClick={() => setIsModalOpen(true)}
          />
        </div>
      ) : (
        <div className='space-y-3'>
          {notifications.map((notification: Notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          totalItems={total}
          itemsPerPage={limit}
          className='mt-4'
        />
      )}

      {/* Send Notification Modal */}
      <SendNotificationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        targetUserId={user.id}
        targetUserName={`${user.firstName} ${user.lastName}`}
      />
    </div>
  );
};

interface NotificationCardProps {
  notification: Notification;
}

const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
}) => {
  const notificationType = notification.type;

  return (
    <div className='p-4 rounded-lg border border-grey-200 bg-white hover:shadow-md transition-shadow'>
      <div className='flex items-start space-x-3'>
        {/* Icon */}
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
            typeColors[notificationType] === 'blue'
              ? 'bg-blue-100 text-blue-600'
              : typeColors[notificationType] === 'green'
                ? 'bg-green-100 text-green-600'
                : typeColors[notificationType] === 'yellow'
                  ? 'bg-yellow-100 text-yellow-600'
                  : typeColors[notificationType] === 'purple'
                    ? 'bg-purple-100 text-purple-600'
                    : 'bg-grey-100 text-grey-600'
          }`}
        >
          <Icon path={typeIcons[notificationType]} size={20} />
        </div>

        {/* Content */}
        <div className='flex-1 min-w-0'>
          <div className='flex items-center justify-between mb-1'>
            <p className='text-sm font-medium text-grey-900 truncate'>
              {notification.title}
            </p>
            <Badge color={typeColors[notificationType]} small>
              {typeLabels[notificationType]}
            </Badge>
          </div>
          <p className='text-sm text-grey-500 line-clamp-2'>
            {notification.body}
          </p>

          {/* Status and Date */}
          <div className='flex items-center justify-between mt-2'>
            <div className='flex items-center space-x-3'>
              {/* Push Status */}
              <div className='flex items-center'>
                <Icon
                  path={notification.isPushSent ? mdiCheck : mdiClose}
                  size={14}
                  className={
                    notification.isPushSent ? 'text-green-500' : 'text-grey-400'
                  }
                />
                <span
                  className={`text-xs ml-1 ${
                    notification.isPushSent ? 'text-green-600' : 'text-grey-500'
                  }`}
                >
                  Push
                </span>
              </div>
              {/* Read Status */}
              <div className='flex items-center'>
                <Icon
                  path={notification.isRead ? mdiCheck : mdiClose}
                  size={14}
                  className={
                    notification.isRead ? 'text-green-500' : 'text-grey-400'
                  }
                />
                <span
                  className={`text-xs ml-1 ${
                    notification.isRead ? 'text-green-600' : 'text-grey-500'
                  }`}
                >
                  Read
                </span>
              </div>
            </div>
            <span className='text-xs text-grey-400'>
              {notification.createdAt
                ? DateTime.fromISO(notification.createdAt).toFormat(
                    'MMM d, yyyy HH:mm',
                  )
                : '-'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
